const _ = require("lodash");
const Font = require("../../util/Font");
const {
	findArguments,
	findOptionalArgument,
	splitArgIntoMathNodes,
} = require("../util/ParserHelpers");
const {drawCurlyBrace} = require("../util/RenderHelpers");
const ColorUtil = require("../../util/ColorUtil");
const diagramColors = ColorUtil.getDiagramColors();
const colorNames = [
	"red",
	"orange",
	"yellow",
	"green",
	"teal",
	"blue",
	"royalblue",
	"purple",
	"pink",
	"vred",
	"vblue",
	"vgreen",
	"vorange",
	"vpurple",
	"vpink",
	"vlightred",
	"vlightblue",
	"vlightgreen",
	"vyellow",
];
const allColorNames = _.keys(diagramColors);
const shapeNames = ["solid", "hollow"];

const parseOptArgType = function(arg) {
	// splits optional args into part before = or space (prefix) and part after (value)
	const getParts = /^([a-zA-Z-_0-9]+)(?:(?: |=)(.*))?$/;
	if (!getParts.test(arg)) {
		return {
			error: `Cannot parse optional argument "${arg}". Optional arguments should be structured as follows: If boolean, just the name of the argument. Otherwise the name, followed by an = or space, followed by the value.`,
		};
	}
	const prefix = arg.match(getParts)[1].toLowerCase();
	const value = arg.match(getParts)[2];
	return {
		prefix,
		value,
	};
};

const parseAllOptionalArgs = function(ps, settings) {
	const optArgs = [];
	while (true) {
		const optArg = findOptionalArgument(ps);
		if (!optArg) {
			break;
		}
		optArgs.push(optArg.getRest());
	}
	const optArgMap = {
		reverse: false,
		noframe: false,
		heading: false,
		boxCount: 1,
		tokenAttributes: {
			color: ["purple", "teal"],
			shape: ["solid", "hollow"],
		},
		tokenModifiers: {},
		labels: [],
		isGrid: false,
	};
	for (const optArg of optArgs) {
		const parsed = parseOptArgType(optArg);
		if (parsed.error) {
			ps.pushError(parsed.error);
			return false;
		} else {
			const prefix = parsed.prefix;
			let value = parsed.value;
			if (_.includes(["reverse", "noframe", "heading"], prefix)) {
				// boolean optional args
				if (value) {
					ps.pushError(
						`Optional argument ${prefix} is expected to be boolean. To use this option, type "[${parsed.prefix}]". To not use this option, remove the optional argument.`
					);
					return false;
				}
				optArgMap[prefix] = true;
			} else if (prefix === settings.boxName + "count") {
				if (!/\d+/.test(value)) {
					ps.pushError(
						`Optional argument ${settings.boxName +
							"count"} is expected to be a nonnegative integer.`
					);
					return false;
				}
				optArgMap.boxCount = parseInt(value);
			} else if (prefix === "colors") {
				const colors = value
					.split(";")
					.map(color => color.trim().toLowerCase());
				if (colors.map(color => colorNames.includes(color)).includes(false)) {
					ps.pushError(
						`Optional argument "colors" is expected to be a semicolon separated list of colors. Allowable colors: ${colorNames.join(
							", "
						)}`
					);
					return false;
				}
				optArgMap.tokenAttributes.color = colors;
			} else if (prefix === "shapes") {
				const shapes = value
					.split(";")
					.map(shape => shape.trim().toLowerCase());
				if (shapes.map(shape => shapeNames.includes(shape)).includes(false)) {
					ps.pushError(
						`Optional argument "shapes" is expected to be a semicolon separated list of shapes. Allowable shapes: solid, hollow.`
					);
					return false;
				}
				optArgMap.tokenAttributes.shape = shapes;
			} else if (["cross", "strike", "erase"].includes(prefix)) {
				const modifier = {type: prefix};
				const regexRanges = /^(?<ranges>(?:\d+ *- *\d+|\d+)(?: *; *(?:\d+ *- *\d+|\d+))*)$/;
				const regexRangesColor = /^(?<ranges>(?:\d+ *- *\d+|\d+)(?: *; *(?:\d+ *- *\d+|\d+))*) +(?<color>[a-zA-Z]+)$/;
				const regexColorRanges = /^(?<color>[a-zA-Z]+) +(?<ranges>(?:\d+ *- *\d+|\d+)(?: *; *(?:\d+ *- *\d+|\d+))*)$/;
				const canHaveColor = prefix === "cross" || prefix === "strike";
				if (!value) {
					ps.pushError(
						`Optional argument "${prefix}" is expected to be a semicolon separated list of token ranges (e.g. 1-5;10;13;15-20;32)${
							canHaveColor ? " and optionally a color" : ""
						}.`
					);
					return false;
				}
				value = value.trim();
				let match = null;
				if (regexRanges.test(value)) {
					match = value.match(regexRanges);
				} else if (canHaveColor && regexRangesColor.test(value)) {
					match = value.match(regexRangesColor);
				} else if (canHaveColor && regexColorRanges.test(value)) {
					match = value.match(regexColorRanges);
				} else {
					ps.pushError(
						`Optional argument "${prefix}" is expected to be a semicolon separated list of token ranges (e.g. 1-5;10;13;15-20;32)${
							canHaveColor ? " and optionally a color" : ""
						}.`
					);
					return false;
				}
				if (canHaveColor) {
					const color = match.groups.color || null;
					if (color && !colorNames.includes(color)) {
						ps.pushError(`Invalid color name: "${color}".`);
						return false;
					}
					modifier.color = color;
				}
				const ranges = match.groups.ranges.split(";").map(range =>
					range
						.trim()
						.split("-")
						.map(n => parseInt(n))
				);
				modifier.ranges = ranges;
				for (const range of ranges) {
					for (let i = range[0] - 1; i < (range[1] || range[0]); i++) {
						if (!optArgMap.tokenModifiers[i.toString()]) {
							optArgMap.tokenModifiers[i.toString()] = [];
						}
						optArgMap.tokenModifiers[i.toString()].push(modifier);
					}
				}
			} else if (prefix === "label") {
				const regexString = / "(?<string>(?:[^"]|\\")*[^\\]|)"/;
				// the following regex string checks whether there is not exactly one unescaped quote following the current position
				const regexNotInQuotes = `(?!(?:|(?:[^"]|\\\\")*[^\\\\])"(?:[^"]|\\\\")*$)`;
				const regexRange = RegExp(
					` (?<range>\\d+-\\d+|\\d+)${regexNotInQuotes}`
				);
				const regexColor = RegExp(
					` (?<color>${allColorNames.join("|")})${regexNotInQuotes}`
				);
				const regexSide = RegExp(` (?<side>left|right)${regexNotInQuotes}`);
				value = value || "";
				value = " " + value.trim();
				if (!value.match(regexString)) {
					ps.pushError(
						`Optional argument label must include a string in quotes (e.g. "this is a label") and a row range (e.g. 4-7 or 2). Can optionally include a color and a side (left or right).`
					);
					return false;
				}
				const string = value.match(regexString).groups.string;
				const rangeMatch = value.match(regexRange);
				const rangeStr = rangeMatch ? rangeMatch.groups.range : null;
				const range = rangeStr
					? rangeStr
							.trim()
							.split("-")
							.map(n => parseInt(n))
					: null;
				const color = _.get(value.match(regexColor), "groups.color", null);
				const side = _.get(value.match(regexSide), "groups.side", null);
				if (!_.isString(string) || !range) {
					ps.pushError(
						`Optional argument label must include a string in quotes (e.g. "this is a label") and a row range (e.g. 4-7 or 2). Can optionally include a color and a side (left or right).`
					);
					return false;
				}
				if (
					value.length !==
					` ${rangeStr} "${string}"`.length +
						(color ? color.length + 1 : 0) +
						(side ? side.length + 1 : 0)
				) {
					ps.pushError(`Optional argument label not properly parseable.`);
					return false;
				}
				let collapsedRange = false;
				if (range) {
					if (!range[1]) {
						range[1] = range[0];
						collapsedRange = true;
					}
					range[0]--;
					range[1]--;
				}
				optArgMap.labels.push({
					string,
					range,
					collapsedRange,
					color,
					side: side || "right",
				});
			} else if (prefix === "frame" && settings.allowFrame) {
				if (!/\d+x\d+;\d+x\d+(?:;\d+;\d+)?/.test(value.trim())) {
					ps.pushError(
						`Optional argument frame not properly parseable. Should be [fragshape];[blockshape] followed optionally by ;[subgroup];[group]. [fragshape] and [blockshape] should be NxN for integer N (e.g. 5x1). [subgroup] and \<group\> should be integers if given.`
					);
					return false;
				}
				const parts = value.trim().split(";");
				optArgMap.frame = {
					fragment: {
						w: parseInt(parts[0].split("x")[0]),
						h: parseInt(parts[0].split("x")[1]),
					},
					box: {
						w: parseInt(parts[1].split("x")[0]),
						h: parseInt(parts[1].split("x")[1]),
					},
					subgroup: parts[2] || 0,
					group: parts[3] || 0,
				};
			} else if (prefix === "grid") {
				optArgMap.isGrid = value === "true";
			} else {
				ps.pushError(
					`Unexpected optional argument "${prefix}". Allowable options: colors, shapes, reverse, noframe, heading, ${settings.boxName}count, cross, strike, erase, label.`
				);
				return false;
			}
		}
	}
	return optArgMap;
};

const makeParser = function(settings = {}) {
	// makes a parser with desired settings
	settings = _.extend(
		{
			boxName: "box",
			frame: {
				fragment: {w: 3, h: 1},
				box: {w: 3, h: 1},
				subgroup: 3,
				group: 9,
			},
			allowFrame: false,
		},
		settings
	);
	return function(ps) {
		const frame = settings.frame;
		// parse the optional arguments
		const optArgMap = parseAllOptionalArgs(ps, settings);
		if (!optArgMap) {
			return false;
		}
		const reqArgs = findArguments(ps, optArgMap.heading ? 2 : 1);
		const tokensStr = reqArgs[0].getRest();
		if (!/^\s*\d+\s*(?:;\s*\d+\s*)*;?$/.test(tokensStr)) {
			ps.pushError(
				`Argument must be a semicolon separated list of nonnegative integers. "${tokensStr}" is not.`
			);
			return false;
		}
		const tokens = tokensStr
			.trim()
			.split(";")
			.map(n => parseInt(n.trim()));
		optArgMap.heading = optArgMap.heading
			? splitArgIntoMathNodes(reqArgs[1])
			: null;
		const tokenAttributes = {
			color: ["purple", "teal"],
			shape: ["solid", "hollow"],
		};
		return [
			{
				type: "numberFrame",
				children: [],
				frame,
				tokens,
				tokenAttributes,
				...optArgMap,
			},
		];
	};
};

const getTokenRect = function(x, y, tokenNum, frame, measure, isGrid = false) {
	const {unit} = measure;
	const fragW = frame.fragment.w;
	const fragH = frame.fragment.h;
	const boxW = fragW * frame.box.w;
	const boxH = fragH * frame.box.h;
	const boxNum = Math.floor(tokenNum / (boxW * boxH));
	const fragNumInBox = Math.floor(
		(tokenNum - boxNum * boxW * boxH) / (fragW * fragH)
	);
	const tokenNumInFrag =
		tokenNum - boxNum * boxW * boxH - fragNumInBox * fragW * fragH;
	const colInBox =
		(fragNumInBox % frame.box.w) * fragW + (tokenNumInFrag % fragW);
	const rowInBox =
		Math.floor(fragNumInBox / frame.box.w) * fragH +
		Math.floor(tokenNumInFrag / fragW);
	return {
		x: x + unit * colInBox,
		y: getBoxRect(x, y, boxNum, frame, measure, isGrid).y + unit * rowInBox,
		w: unit,
		h: unit,
	};
};

const drawToken = function(ctx, x, y, color, shape, measure, mod = []) {
	const {unit, spacing, thickness} = measure;
	const r = spacing.tokenRadius;
	const innerR = spacing.tokenInnerRadius;
	const fade =
		mod.filter(m => m.type === "strike" || m.type === "cross").length > 0;
	const erase = mod.filter(m => m.type === "erase").length > 0;
	const arcMain = () => ctx.arc(x, y, r, 0, Math.PI * 2);
	const arcInner = () => ctx.arc(x, y, innerR, 0, Math.PI * 2, true);
	const stroke = () => {
		if (!fade) {
			ctx.stroke();
		}
	};
	ctx.save();
	ctx.fillStyle = diagramColors[color];
	ctx.strokeStyle = diagramColors[`dark${_.capitalize(color)}`];
	ctx.lineWidth = thickness.tokenOutline;
	if (fade) {
		ctx.fillStyle = diagramColors[`faded${_.capitalize(color)}`];
	}
	if (!erase) {
		if (shape === "solid") {
			ctx.beginPath();
			arcMain();
			ctx.fill();
			stroke();
		}
		if (shape === "hollow") {
			ctx.beginPath();
			arcMain();
			arcInner();
			ctx.fill();
			ctx.beginPath();
			arcMain();
			stroke();
			ctx.beginPath();
			arcInner();
			stroke();
		}
	}
	for (const m of mod) {
		if (m.type === "cross") {
			ctx.strokeStyle = m.color
				? diagramColors[m.color]
				: diagramColors.mediumDarkGray;
			ctx.lineWidth = thickness.strikeout;
			ctx.beginPath();
			ctx.M(x - r, y - r);
			ctx.L(x + r, y + r);
			ctx.M(x + r, y - r);
			ctx.L(x - r, y + r);
			ctx.stroke();
		} else if (m.type === "strike") {
			ctx.strokeStyle = m.color
				? diagramColors[m.color]
				: diagramColors.mediumDarkGray;
			ctx.lineWidth = thickness.strikeout;
			ctx.beginPath();
			ctx.M(x - unit / 2, y);
			ctx.L(x + unit / 2, y);
			ctx.stroke();
		}
	}
	ctx.restore();
};

const getBoxRect = function(x, y, boxNum, frame, measure, isGrid = false) {
	const {unit, spacing} = measure;
	const boxW = frame.fragment.w * frame.box.w;
	const boxH = frame.fragment.h * frame.box.h;
	const gridGapOffset = isGrid ? boxNum * boxH * spacing.gridRowGap : 0;
	return {
		x: x,
		y:
			y +
			unit * boxH * boxNum +
			(frame.group ? spacing.groupGap * Math.floor(boxNum / frame.group) : 0) +
			gridGapOffset,
		w: unit * boxW,
		h: unit * boxH,
	};
};

const drawBox = function(
	ctx,
	boxNum,
	rect,
	frame,
	measure,
	isGrid,
	totalBoxes = 1
) {
	const {unit, spacing, thickness} = measure;
	ctx.save();
	ctx.fillStyle = diagramColors.veryLightGray;
	if (isGrid) {
		ctx.strokeStyle = diagramColors.mediumDarkGray;
	} else {
		ctx.strokeStyle = diagramColors.mediumLightGray;
	}
	ctx.lineWidth = thickness.frame;
	ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
	ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
	// draw the fragment separators - the fragments are the two parts of the ten frame, or row lines in a tenstrip
	ctx.lineWidth = thickness.fragmentDivider;
	ctx.save();
	if (isGrid) {
		ctx.strokeStyle = diagramColors.mediumDarkGray;
		ctx.lineWidth = thickness.frame;
	}
	ctx.beginPath();
	for (let i = 1; i < frame.box.w; i++) {
		ctx.M(
			rect.x + i * frame.fragment.w * unit,
			rect.y + (isGrid ? 0 : spacing.fragmentDividerSpacing)
		);
		ctx.L(
			rect.x + i * frame.fragment.w * unit,
			rect.y + rect.h - (isGrid ? 0 : spacing.fragmentDividerSpacing)
		);
	}
	ctx.stroke();
	ctx.restore();
	for (let j = 1; j < frame.box.h; j++) {
		ctx.M(
			rect.x + spacing.fragmentDividerSpacing,
			rect.y + j * frame.fragment.h * unit
		);
		ctx.L(
			rect.x + rect.w - spacing.fragmentDividerSpacing,
			rect.y + j * frame.fragment.h * unit
		);
	}
	ctx.stroke();

	// Draw vertical grid lines for each cell when isGrid is true
	if (isGrid) {
		ctx.save();
		ctx.strokeStyle = diagramColors.mediumGray;
		ctx.lineWidth = thickness.fragmentDivider;
		ctx.beginPath();

		// Determine which lines to draw based on box position
		const boxWidth = frame.box.w * frame.fragment.w;

		for (let i = 1; i < boxWidth; i++) {
			if (i % 5 === 0) {
				continue;
			}
			ctx.M(rect.x + i * unit, rect.y + 2);
			ctx.L(rect.x + i * unit, rect.y + rect.h - 2);
		}

		ctx.stroke();
		ctx.restore();
	}

	if (boxNum % frame.group !== 0 && boxNum % frame.subgroup === 0) {
		ctx.lineWidth = thickness.subgroupDivider;
		ctx.beginPath();
		ctx.M(rect.x, rect.y);
		ctx.L(rect.x + rect.w, rect.y);
		ctx.stroke();
	}
	ctx.restore();
};

const getRowRect = function(x, y, rowNum, frame, measure, isGrid = false) {
	const {unit, spacing} = measure;
	const boxW = frame.fragment.w * frame.box.w;
	const boxH = frame.fragment.h * frame.box.h;
	const boxNum = Math.floor(rowNum / boxH);
	const rowInBox = rowNum - boxNum * boxH;
	const gridGapOffset = isGrid ? rowNum * spacing.gridRowGap : 0;
	return {
		x: x,
		y:
			getBoxRect(x, y, boxNum, frame, measure, isGrid).y +
			unit * rowInBox +
			gridGapOffset,
		w: unit * boxW,
		h: unit,
	};
};

const drawNumberFrame = function(ctx, x, y, node, measure) {
	const {unit, numBoxes} = measure;
	ctx.save();
	if (!node.noframe) {
		for (let b = 0; b < numBoxes; b++) {
			drawBox(
				ctx,
				b,
				getBoxRect(x, y, b, node.frame, measure, node.isGrid),
				node.frame,
				measure,
				node.isGrid,
				numBoxes
			);
		}
	}
	const tokens = _.flatten(
		node.tokens.map((n, k) =>
			_.fill(Array(n), k).map(tokenType => ({tokenType}))
		)
	);
	for (const pair of _.toPairs(tokens)) {
		const index = pair[0];
		const token = pair[1];
		_.extend(
			token,
			_.mapValues(
				node.tokenAttributes,
				list =>
					list[
						Math.min(
							node.reverse
								? node.tokens.length - token.tokenType - 1
								: token.tokenType,
							list.length - 1
						)
					]
			)
		);
		tokens[index].mod = node.tokenModifiers[index] || [];
	}
	_.forIn(tokens, (token, index) => {
		const tokenRect = getTokenRect(
			x,
			y,
			index,
			node.frame,
			measure,
			node.isGrid
		);
		drawToken(
			ctx,
			tokenRect.x + unit / 2,
			tokenRect.y + unit / 2,
			token.color,
			token.shape,
			measure,
			token.mod,
			node.isGrid
		);
	});
	ctx.restore();
};

const getLabelPushOut = function(
	label,
	labels,
	ctx,
	measure,
	includeSelf = false
) {
	const {spacing} = measure;
	const labelPadding =
		(label.collapsedRange ? 2 : 3) * spacing.labelGap +
		(label.collapsedRange ? 0 : spacing.brace);
	const labelSize = ctx.measureText(label.string).width + labelPadding;
	const innerLabels = labels.filter(
		x =>
			x.side === label.side &&
			label.range[0] <= x.range[0] &&
			x.range[1] <= label.range[1] &&
			(label.range[0] < x.range[0] || x.range[1] < label.range[1])
	);
	if (innerLabels.length === 0) {
		return includeSelf ? labelSize : 0;
	}
	return (
		Math.max(
			...innerLabels.map(innerLabel =>
				getLabelPushOut(innerLabel, labels, ctx, measure, true)
			)
		) + (includeSelf ? labelSize : 0)
	);
};

const measureLabels = function(labels, ctx, measure) {
	const measuredBounds = labels.reduce(
		(bounds, label) => {
			bounds[label.side] = Math.max(
				bounds[label.side],
				getLabelPushOut(label, labels, ctx, measure, true)
			);
			return bounds;
		},
		{right: 0, left: 0}
	);
	return {
		right: Math.max(0, measuredBounds.right),
		left: Math.max(0, measuredBounds.left),
	};
};

const drawLabels = function(ctx, x, y, labels, frame, measure, isGrid = false) {
	const {unit, spacing} = measure;
	const boxW = frame.fragment.w * frame.box.w;
	for (const label of labels) {
		const side = label.side;
		const color = diagramColors[label.color] || "mediumDarkGray";
		const collapsedRange = label.collapsedRange;
		const pushOut = getLabelPushOut(label, labels, ctx, measure); // used to get how far out nested labels should be
		ctx.save();
		ctx.fillStyle = color;
		ctx.strokeStyle = color;
		const topRow = getRowRect(x, y, label.range[0], frame, measure, isGrid);
		const bottomRow = getRowRect(
			x,
			y,
			label.range[1] || label.range[0],
			frame,
			measure,
			isGrid
		);
		const labelTop = topRow.y + spacing.labelInset;
		const labelBottom = bottomRow.y + bottomRow.h - spacing.labelInset;
		const labelHeight = labelBottom - labelTop;
		const labelCenter = labelTop + labelHeight / 2;
		const labelXBase = side === "left" ? x : x + unit * boxW;
		const sideFactor = side === "left" ? -1 : 1;
		const labelX = labelXBase + sideFactor * (pushOut + spacing.labelGap);
		const labelTextX = labelX + sideFactor * (spacing.brace + spacing.labelGap);
		ctx.textBaseline = "middle";
		ctx.textAlign = side === "left" ? "right" : "left";
		if (!collapsedRange) {
			drawCurlyBrace(ctx, labelHeight, labelX, labelTop, side[0]);
		}
		ctx.fillText(
			label.string,
			collapsedRange ? labelX : labelTextX,
			labelCenter
		);
		ctx.restore();
	}
};

const drawHeading = function(rc, x, y, heading, frame, measure) {
	const {unit, spacing, headingMeasure} = measure;
	const boxW = frame.fragment.w * frame.box.w;
	rc.context.save();
	if (heading && heading.length > 0) {
		rc.drawNode(
			rc,
			x + (unit * boxW) / 2 - headingMeasure.width / 2,
			y - headingMeasure.bottom - spacing.headingGap,
			heading[0],
			headingMeasure
		);
	}
	rc.context.restore();
};

const numberFrameNode = {
	draw(rc, x, y, node, measure) {
		const ctx = rc.context;
		const {top, headingHeight, labelBounds, fontInfo} = measure;
		x = x + labelBounds.left;
		y = y - top + headingHeight;
		ctx.save();
		ctx.font = Font.fromInfo(fontInfo);
		drawNumberFrame(ctx, x, y, node, measure);
		drawHeading(rc, x, y, node.heading, node.frame, measure);
		drawLabels(ctx, x, y, node.labels, node.frame, measure, node.isGrid);
		ctx.restore();
	},
	measure(rc, fontInfo, props, node) {
		// universal constants based on fontSize
		const fontSize = fontInfo.size;
		const unit = fontSize * 1.5;
		const pt = unit / 72;
		const padding = unit / 7;
		const tokenRadius = unit / 2 - padding;
		const spacing = {
			padding: padding,
			groupGap: unit / 3,
			headingGap: unit / 3,
			labelGap: unit / 3,
			labelInset: 9 * pt,
			tokenRadius: tokenRadius,
			tokenInnerRadius: tokenRadius / 2,
			fragmentDividerSpacing: 8 * pt,
			brace: 9 * pt,
			gridRowGap: unit / 8, // gap between rows when isGrid is true
		};
		const thickness = {
			fragmentDivider: 2 * pt,
			tokenOutline: 2 * pt,
			frame: 4 * pt,
			subgroupDivider: 6 * pt,
			strikeout: 9 * pt,
		};
		// node-specific values (for the main numberframe part)
		const totalTokens = node.tokens.reduce((a, b) => a + b);
		const rowLength = node.frame.fragment.w * node.frame.box.w;
		const boxRows = node.frame.fragment.h * node.frame.box.h;
		const numBoxes = Math.max(
			Math.ceil(totalTokens / (rowLength * boxRows)),
			node.boxCount
		);
		const numRows = boxRows * numBoxes;
		const numGaps =
			node.frame.group === 0
				? 0
				: Math.floor(numBoxes / node.frame.group) -
				  (numBoxes % node.frame.group === 0 ? 1 : 0);
		const gridGapHeight = node.isGrid ? numRows * spacing.gridRowGap : 0;
		const height = numRows * unit + numGaps * spacing.groupGap + gridGapHeight;
		const width = rowLength * unit;
		// node-specific values (for the heading)
		rc.context.save();
		const headingMeasure =
			node.heading && node.heading.length > 0
				? rc.measureNode(rc, fontInfo, props, node.heading[0])
				: {width: 0, top: 0, bottom: 0};
		rc.context.restore(); // for some reason, measuring the heading (if it had \bigger) could change the font size
		let headingHeight = headingMeasure.top + headingMeasure.bottom;
		headingHeight = headingHeight > 0 ? headingHeight + spacing.headingGap : 0;
		// node-specific values (for the labels)
		rc.context.save();
		rc.context.font = Font.fromInfo(fontInfo);
		const labelBounds = measureLabels(node.labels, rc.context, {spacing});
		rc.context.restore();
		// node-specific values (for the noframe option)
		const shrinkFrame =
			node.noframe &&
			labelBounds.left + labelBounds.right === 0 &&
			!node.heading;
		const w = shrinkFrame
			? Math.min(width, unit * totalTokens)
			: width + labelBounds.left + labelBounds.right;
		const shrunkRows = Math.ceil(totalTokens / rowLength);
		const shrunkGridGapHeight = node.isGrid
			? shrunkRows * spacing.gridRowGap
			: 0;
		const h = shrinkFrame
			? Math.min(
					height,
					unit * shrunkRows + numGaps * spacing.groupGap + shrunkGridGapHeight
			  )
			: height;
		return {
			width: w,
			top: h / 2 + headingHeight,
			bottom: h / 2,
			unit,
			pt,
			spacing,
			thickness,
			numBoxes,
			fontInfo,
			headingMeasure,
			headingHeight,
			labelBounds,
		};
	},
	readAloud(node, context) {
		return "";
	},
};

module.exports = {
	parsers: {
		mathCommands: {
			tenframe: makeParser({
				boxName: "box",
				frame: {
					fragment: {w: 5, h: 1},
					box: {w: 1, h: 2},
					subgroup: 0, // no thick lines
					group: 1, // gap between every tenframe
				},
			}),
			tenstrip: makeParser({
				boxName: "row",
				frame: {
					fragment: {w: 5, h: 1},
					box: {w: 2, h: 1},
					subgroup: 5, // thick line at 50
					group: 10, // gap between 100s
				},
			}),
			numberframe: makeParser({
				allowFrame: true,
			}),
		},
	},
	nodes: {
		numberFrame: numberFrameNode,
	},
};
